import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { WaveSine, PlayCircle, ChartLineUp, Users, Sparkle, Megaphone, Buildings } from '@phosphor-icons/react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Landing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();

  const publicHome = searchParams.get('public') === '1';

  useEffect(() => {
    if (user && !publicHome) navigate('/dashboard');
  }, [user, publicHome, navigate]);

  const handleLogin = (dest = '/dashboard') => {
    if (user) {
      navigate(dest);
      return;
    }

    window.location.href = `/api/auth/google/start?next=${encodeURIComponent(dest)}`;
  };

  if (loading) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 relative overflow-hidden">
      {/* Hero */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <img src="https://images.pexels.com/photos/18197120/pexels-photo-18197120.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
             alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/40 via-zinc-950/70 to-zinc-950" />
      </div>

      <div className="relative">
        {/* Nav */}
        <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WaveSine size={26} weight="duotone" className="text-[#ccff00]" />
            <span className="font-display text-xl font-bold tracking-tight">TUNEAMPLIFIER</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" data-testid="landing-login-link" className="text-sm font-mono text-zinc-400 hover:text-[#ccff00] transition-colors">Log in</Link>
            <button
              data-testid="landing-login-btn"
              onClick={() => handleLogin('/dashboard')}
              className="btn-neon px-5 py-2 rounded-full text-sm font-semibold font-display"
            >
              Sign in with Google
            </button>
          </div>
        </nav>

        {/* Hero */}
        <header className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-800 bg-zinc-900/60 text-xs font-mono text-zinc-400 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ccff00]" /> find the right curators
            </div>
            <h1 className="font-display font-bold text-5xl md:text-7xl lg:text-[92px] leading-[0.95] tracking-tight">
              Push your <span className="text-[#ccff00]">sound</span><br />
              past the algorithm.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-zinc-400 leading-relaxed">
              TuneAmplifier promotes music on Spotify & YouTube, drafts your socials with AI,
              runs cheap ads on Google, Meta and TikTok, and pitches to record labels
              <span className="text-zinc-300"> and suitable playlist curators</span>.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <Link
                data-testid="hero-cta-btn"
                to="/register"
                className="btn-neon px-6 py-3 rounded-full font-display font-semibold flex items-center gap-2"
              >
                <PlayCircle size={20} weight="fill" /> Start free — 5 credits
              </Link>
              <a href="#how" className="px-6 py-3 rounded-full border border-zinc-700 hover:border-zinc-500 text-sm font-mono transition-[border-color]">
                How it works
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 border border-zinc-800 bg-zinc-900/60 backdrop-blur-xl rounded-2xl p-6">
            <div className="font-mono text-xs text-zinc-500 mb-3">// this week for artists on tuneamplifier</div>
            <div className="space-y-4 stagger-in">
              <Stat label="Curator Submissions" value="Find relevant curators" />
              <Stat label="Social Media" value="AI-powered content & posting" />
              <Stat label="Paid Advertising" value="Google / Meta / TikTok" />
              <Stat label="Label Submissions" value="Direct label demos" />
            </div>
          </div>
        </header>

        {/* Feature grid — 5 clickable entry points */}
        <section id="how" className="max-w-7xl mx-auto px-6 pb-24 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Feature onClick={() => handleLogin('/dashboard')} testid="feature-dashboard" icon={<ChartLineUp size={22} weight="duotone" />} title="Spotify + YouTube analytics" body="Track streams, saves, subscribers, watch hours — all in one dark dashboard." />
          <Feature onClick={() => handleLogin('/create')} testid="feature-social-promo" icon={<Sparkle size={22} weight="duotone" />} title="AI Social Media Posting" body="AI writes native captions per platform, generates photos & videos (Nano Banana, GPT Image 1, Sora 2, Kling 2.1), then posts or schedules for you." />
          <Feature onClick={() => handleLogin('/ads')} testid="feature-ads" icon={<Megaphone size={22} weight="duotone" />} title="Cheap ads · Google / Meta / TikTok" body="Set a budget. AI targets the right listeners for your genre — or dial it in yourself." />
          <Feature onClick={() => handleLogin('/marketplace')} testid="feature-curators" icon={<Users size={22} weight="duotone" />} title="Find suitable curators" body="Submit to relevant curators. Credits are charged for consideration, not for playlist placement." />
          <Feature onClick={() => handleLogin('/labels')} testid="feature-labels" icon={<Buildings size={22} weight="duotone" />} title="Label demo submissions" body="Send your demo to one or many labels — flat 5 credits per song, LabelRadar-style." />
        </section>
      </div>

        <footer className="max-w-7xl mx-auto px-6 pb-8 pt-4">
          <div className="border-t border-zinc-800/70 pt-6 text-center">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] font-mono text-zinc-600">
              <span>© 2026 TuneAmplifier</span>
              <span className="text-zinc-800">·</span>
              <a
                href="/privacy-policy"
                className="hover:text-zinc-300 transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-zinc-800">·</span>
              <a
                href="/terms"
                className="hover:text-zinc-300 transition-colors"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </footer>
    </div>
  );
}

function Stat({ label, value, delta, positive }) {
  return (
    <div className="flex items-baseline justify-between border-b border-zinc-800/60 pb-3 last:border-0">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="font-display text-2xl mt-0.5">{value}</div>
      </div>
      <div className={`text-xs font-mono ${positive ? 'text-[#ccff00]' : 'text-zinc-400'}`}>{delta}</div>
    </div>
  );
}

function Feature({ icon, title, body, onClick, testid }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      className="text-left border border-zinc-800 rounded-xl p-5 bg-zinc-900/40 card-hover focus-visible:border-[#ccff00]"
    >
      <div className="w-9 h-9 rounded-lg border border-zinc-800 flex items-center justify-center text-[#ccff00] mb-4">{icon}</div>
      <h3 className="font-display text-lg mb-1">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </button>
  );
}
